/**
 * Per-bot calibration limits, published by aistudio on the bot record as the
 * `CALIBRATION_CONFIG` bot property. Pure parsing/selection helpers live here;
 * `use-calibration-config.ts` wraps them in the reactive hook that reads
 * `@frontend-platform/host-state`.
 *
 * Port of aistudio's `useCalibrationConfig` composable
 * (client/views/Calibration/composables/useCalibrationConfig.js) — the defaults
 * and key mapping below are deliberately identical, since both apps must agree
 * on the caps they enforce and the numbers they quote to the user.
 */

export interface CalibrationConfig {
  conversationLowerLimit: number;
  conversationUpperLimit: number;
  /**
   * Cap for every non-production environment. Env promotion means there can be
   * several (Staging, QA, UAT…), so the legacy staging-only key is only a
   * fallback now.
   */
  conversationUpperLimitNonProd: number;
  dateRangeCapDays: number;
  maxRunLimit: number;
  customMetricLimit: number;
}

export const CALIBRATION_CONFIG_DEFAULTS: CalibrationConfig = {
  conversationLowerLimit: 250,
  conversationUpperLimit: 1000,
  conversationUpperLimitNonProd: 250,
  dateRangeCapDays: 30,
  maxRunLimit: 15,
  customMetricLimit: 5,
};

/**
 * Maps the bot property's SCREAMING_SNAKE payload onto {@link CalibrationConfig}.
 * `raw` is whatever `properties.CALIBRATION_CONFIG.value` holds — a JSON string
 * in practice, but an already-parsed object is accepted too.
 *
 * Note `conversationLowerLimit` is intentionally NOT configurable: aistudio
 * always takes it from its defaults, so honouring an override here would let
 * the two apps disagree on the minimum viable run size.
 */
export function parseCalibrationConfig(raw: unknown): CalibrationConfig {
  if (!raw) return CALIBRATION_CONFIG_DEFAULTS;
  try {
    const parsed = (typeof raw === 'string' ? JSON.parse(raw) : raw) as Record<string, unknown>;
    if (!parsed || typeof parsed !== 'object') return CALIBRATION_CONFIG_DEFAULTS;
    const num = (value: unknown, fallback: number): number =>
      typeof value === 'number' && Number.isFinite(value) ? value : fallback;
    return {
      conversationLowerLimit: CALIBRATION_CONFIG_DEFAULTS.conversationLowerLimit,
      conversationUpperLimit: num(
        parsed.CONVERSATION_UPPER_LIMIT,
        CALIBRATION_CONFIG_DEFAULTS.conversationUpperLimit
      ),
      conversationUpperLimitNonProd: num(
        parsed.CONVERSATION_UPPER_LIMIT_NON_PROD ?? parsed.CONVERSATION_UPPER_LIMIT_STAGING,
        CALIBRATION_CONFIG_DEFAULTS.conversationUpperLimitNonProd
      ),
      dateRangeCapDays: num(
        parsed.DATE_RANGE_CAPPING_IN_DAYS,
        CALIBRATION_CONFIG_DEFAULTS.dateRangeCapDays
      ),
      maxRunLimit: num(parsed.MAX_RUN_LIMIT, CALIBRATION_CONFIG_DEFAULTS.maxRunLimit),
      customMetricLimit: num(
        parsed.CUSTOM_METRIC_LIMIT,
        CALIBRATION_CONFIG_DEFAULTS.customMetricLimit
      ),
    };
  } catch {
    // Malformed JSON on the bot property must not break the wizard — fall back
    // to the same defaults aistudio uses.
    return CALIBRATION_CONFIG_DEFAULTS;
  }
}

/**
 * Single source of truth for which conversation cap applies, so the Scope
 * step's banner and the submit payload can't drift on the limit a non-prod
 * data source gets — the banner previously told the user one cap while the
 * payload submitted against the other.
 */
export function getConversationUpperLimit(
  isNonProd: boolean,
  config: CalibrationConfig = CALIBRATION_CONFIG_DEFAULTS
): number {
  return isNonProd ? config.conversationUpperLimitNonProd : config.conversationUpperLimit;
}
